<template>
  <div class="credits-result-page">
    <div class="result-card">
      <div class="result-icon success">
        ✓
      </div>

      <h2 class="result-title">
        {{ ui.title }}
      </h2>

      <p class="result-subtitle">
        <span v-if="loading">{{ ui.verifying }}</span>
        <span v-else-if="error">{{ error }}</span>
        <span v-else>
          {{ ui.balanceUpdated }}
        </span>
      </p>

      <div
        v-if="!loading && !error"
        class="result-balance"
      >
        <span class="rb-label">{{ ui.currentBalance }}</span>
        <span class="rb-value">{{ balance }} pts</span>
      </div>

      <div class="result-actions">
        <button
          type="button"
          class="btn-primary"
          :disabled="loading"
          @click="goHome"
        >
          {{ ui.continue }}
        </button>
        <button
          type="button"
          class="btn-ghost"
          :disabled="loading"
          @click="openRecharge"
        >
          {{ ui.viewPacks }}
        </button>
      </div>

      <p class="result-hint">
        {{ ui.hint }}
      </p>
    </div>
  </div>
</template>

<script>
import { getUserData, getHomeRouteForLoggedInUser, isUserLoggedIn } from '@/auth/utils'

export default {
  name: 'CreditsSuccess',
  data() {
    return {
      loading: true,
      error: null,
    }
  },
  computed: {
    isUiEn() {
      const l = String(this.$i18n?.locale || 'fr').toLowerCase()
      return l === 'en' || l.startsWith('en-')
    },
    ui() {
      if (this.isUiEn) {
        return {
          title: 'Payment confirmed',
          verifying: 'Verifying…',
          balanceUpdated: 'Your balance has been updated.',
          currentBalance: 'Current balance',
          continue: 'Continue',
          viewPacks: 'View packs',
          hint: 'If your balance looks wrong, refresh the page or sign in again.',
          errNoSession: 'Missing Stripe session.',
          errLogin: 'Please sign in to complete verification.',
          errVerify: 'Could not verify payment.',
        }
      }
      return {
        title: 'Paiement confirmé',
        verifying: 'Vérification en cours…',
        balanceUpdated: 'Votre solde a été mis à jour.',
        currentBalance: 'Solde actuel',
        continue: 'Continuer',
        viewPacks: 'Voir les packs',
        hint: 'Si vous ne voyez pas votre solde, rechargez la page ou reconnectez-vous.',
        errNoSession: 'Session Stripe manquante.',
        errLogin: 'Veuillez vous connecter pour finaliser la vérification.',
        errVerify: 'Erreur lors de la vérification du paiement.',
      }
    },
    balance() {
      return this.$store.getters['credits/balance']
    },
  },
  async mounted() {
    const sessionId = this.$route.query.session_id
    if (!sessionId) {
      this.loading = false
      this.error = this.ui.errNoSession
      return
    }

    if (!isUserLoggedIn()) {
      this.loading = false
      this.error = this.ui.errLogin
      return
    }

    try {
      await this.$store.dispatch('credits/verifyCheckoutSession', { sessionId })
    } catch (e) {
      this.error = e?.response?.data?.error || e?.message || this.ui.errVerify
    } finally {
      this.loading = false
    }
  },
  methods: {
    goHome() {
      const userData = getUserData()
      this.$router.push(getHomeRouteForLoggedInUser(userData ? userData.role : null)).catch(() => {})
    },
    openRecharge() {
      this.$store.dispatch('credits/openRechargeModal', { message: '' }).catch(() => {})
    },
  },
}
</script>

<style scoped>
.credits-result-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: radial-gradient(1200px 500px at 10% 10%, rgba(56, 182, 255, 0.18), transparent 60%),
    radial-gradient(800px 400px at 90% 20%, rgba(16, 185, 129, 0.14), transparent 55%),
    #0b1220;
}
.result-card {
  background: #fff;
  border-radius: 22px;
  padding: 34px 30px 28px;
  width: 100%;
  max-width: 520px;
  text-align: center;
  box-shadow: 0 28px 72px rgba(0, 0, 0, 0.32);
}
.result-icon {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 34px;
  font-weight: 900;
  margin: 0 auto 14px;
}
.result-icon.success {
  background: rgba(16, 185, 129, 0.14);
  color: #047857;
}
.result-title {
  font-weight: 900;
  color: #0f172a;
  margin-bottom: 6px;
  font-size: 1.4rem;
}
.result-subtitle {
  color: #475569;
  margin-bottom: 18px;
  line-height: 1.5;
}
.result-balance {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 12px 14px;
  margin: 0 auto 18px;
  max-width: 360px;
}
.rb-label {
  color: #64748b;
  font-weight: 700;
}
.rb-value {
  color: #0f172a;
  font-weight: 900;
}
.result-actions {
  display: flex;
  gap: 10px;
  justify-content: center;
  flex-wrap: wrap;
}
.btn-primary {
  border: none;
  background: linear-gradient(135deg, #38B6FF, #0096e6);
  color: #fff;
  padding: 12px 20px;
  border-radius: 14px;
  font-weight: 900;
  cursor: pointer;
}
.btn-primary:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
.btn-ghost {
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #0f172a;
  padding: 12px 18px;
  border-radius: 14px;
  font-weight: 900;
  cursor: pointer;
}
.btn-ghost:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
.result-hint {
  margin-top: 14px;
  color: #94a3b8;
  font-size: 0.82rem;
}
</style>
